/**
 * Word-level diff for the KB version history — Step 122.
 *
 * The reference shows version history only after an article has been
 * "updated more than once", and shows no diff at all. A history without a
 * diff is a list of dates. This is the smallest thing that is genuinely a
 * diff: longest-common-subsequence over words, so "changed one sentence in a
 * 900-word article" renders as one sentence, not two walls of text.
 *
 * Quadratic in the number of words; articles are a few thousand words at
 * most and this runs on the client for one pair at a time. Not for code.
 */
export type DiffOp = { kind: 'same' | 'added' | 'removed'; text: string };

const tokenise = (s: string) => s.split(/(\s+)/).filter((t) => t.length > 0);

export function wordDiff(before: string, after: string): DiffOp[] {
  const a = tokenise(before);
  const b = tokenise(after);
  // LCS table, one row at a time would do but the sizes are small.
  const m = a.length;
  const n = b.length;
  const dp: Uint32Array[] = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const ops: DiffOp[] = [];
  const push = (kind: DiffOp['kind'], text: string) => {
    const last = ops[ops.length - 1];
    if (last && last.kind === kind) last.text += text;
    else ops.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      push('same', a[i]!);
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      push('removed', a[i]!);
      i++;
    } else {
      push('added', b[j]!);
      j++;
    }
  }
  while (i < m) push('removed', a[i++]!);
  while (j < n) push('added', b[j++]!);
  return ops;
}

/** How much changed, for a one-line summary: "+42 −7 words". */
export function diffSummary(ops: DiffOp[]): { added: number; removed: number } {
  const words = (t: string) => t.split(/\s+/).filter(Boolean).length;
  return {
    added: ops.filter((o) => o.kind === 'added').reduce((n, o) => n + words(o.text), 0),
    removed: ops.filter((o) => o.kind === 'removed').reduce((n, o) => n + words(o.text), 0),
  };
}
